import { Fragment, useContext, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import cn from 'classnames';
import Image from 'next/future/image';
import { QsContext } from '@/lib/use-qs';
import downIcon from '@/public/icons/collection/chevron_down.svg';
import i18n from '@/settings/i18n';
import useRouterTransition from '@/lib/use-router-transition';

const t = i18n.init();

type SortValue = 'price-asc' | 'price-desc' | 'featured';

type SortType = { name: string; value: SortValue; order: 'asc' | 'desc' };

const sortList: Array<SortType> = [
  { name: 'Featured', value: 'featured', order: 'asc' },
  { name: t`Price (Low to High)`, value: 'price-asc', order: 'asc' },
  { name: t`Price (High to Low)`, value: 'price-desc', order: 'desc' },
];

const CustomRadio = (props: { isChecked: boolean; positionClass: string }) => {
  const { isChecked, positionClass } = props;

  return (
    <span
      className={cn(
        'absolute inline-block h-[6px] w-[6px] -translate-y-1/2 rounded-full ring-gray-700 ring-offset-white xs:h-2 xs:w-2',
        positionClass,
        {
          'border border-gray-700 bg-gray-700 ring-[1px] ring-offset-[2px] ':
            isChecked,
        },
        {
          'border-none bg-white ring-[1px] ring-offset-[1.5px] ': !isChecked,
        },
      )}
    ></span>
  );
};

function SortSelect(props: {
  shouldShowTopBar?: boolean;
  isScrolling?: boolean;
  handleDropdownOpen: (toggle: boolean) => void;
  isDropDownOpen: boolean;
  dropdownRef: React.RefObject<HTMLButtonElement>;
}) {
  const { handleDropdownOpen, isDropDownOpen, dropdownRef } = props;
  const isTransitioning = useRouterTransition();

  const [qs, updateQs] = useContext(QsContext);
  const [sortSelected, setSortSelected] = useState(
    sortList.find((sort) => sort.value === qs.sort) || sortList[0],
  );

  const handleSortChange = (s: SortType) => {
    setSortSelected(s);
    updateQs({
      order: s.value === 'featured' ? undefined : s.order,
      sort: s.value === 'featured' ? undefined : s.value,
    });
  };

  return (
    <Listbox
      value={sortSelected}
      onChange={(sort) => {
        handleDropdownOpen(false);
        handleSortChange(sort);
      }}
    >
      <div
        className={cn(
          'relative z-20',
          {
            'pointer-events-none opacity-30': isTransitioning,
          },
          {
            'opacity-100': !isTransitioning,
          },
        )}
      >
        <Listbox.Button
          ref={dropdownRef}
          className={cn(
            'w-full cursor-pointer rounded-r-full border-2 border-l-0 border-gray-700 bg-white py-[14px] pl-3 pr-8 text-center text-xs font-bold uppercase focus:outline-none focus-visible:border-gray-500 xs:py-4 xs:pl-4 xs:pr-10 xs:text-sm md:text-base lg:relative lg:!rounded-none lg:border lg:!border-l lg:border-b lg:border-gray-200 lg:py-2 lg:text-left lg:text-sm lg:font-normal lg:capitalize',
            {
              '!rounded-none !rounded-tl-2xl !rounded-tr-2xl !border-l-2 border-b-0 !py-[15px] !pl-6 !pr-12 text-left xs:!py-4 xs:!pl-9 xs:!pr-16 lg:!py-2 lg:!pl-4 lg:!pr-10':
                isDropDownOpen,
            },
          )}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            handleDropdownOpen(true);
          }}
        >
          <span
            className={cn('block truncate', {
              'text-left': isDropDownOpen,
            })}
          >
            {sortSelected.name}
          </span>
          <span
            className={cn(
              'absolute right-4 top-1/2 flex w-4 -translate-y-1/2 items-center transition duration-150 ease-out xs:right-6 lg:right-3',
              {
                '!right-5 rotate-180 xs:!right-8 lg:!right-3': isDropDownOpen,
              },
            )}
          >
            <div className="relative h-[6px] w-[12px] xs:h-[7px] xs:w-[14px]">
              <Image
                src={downIcon}
                loading="eager"
                alt="down"
                className="w-full object-contain"
              />
            </div>
          </span>
        </Listbox.Button>
        <Transition as={Fragment} show={isDropDownOpen}>
          <Listbox.Options
            className={cn(
              'absolute w-full overflow-auto rounded-none rounded-bl-2xl rounded-br-2xl border-2 border-t-0 border-gray-700 bg-white px-3 py-1 pt-2 text-xs shadow-none focus:outline-none xs:px-6 xs:py-2 xs:text-sm lg:w-full lg:rounded-md lg:border-gray-200 lg:p-0 lg:py-1 lg:shadow-lg',
            )}
          >
            {isDropDownOpen && (
              <span className="absolute left-0 top-0 block h-[2px] w-full bg-gray-200 lg:hidden"></span>
            )}
            {sortList.map((sort, sortIdx) => (
              <Listbox.Option
                key={sortIdx}
                className={cn(
                  'relative cursor-pointer select-none py-[10px] pl-9 text-gray-700 xs:py-3 xs:pl-10 lg:py-2 lg:pl-10 lg:pr-4',
                )}
                value={sort}
              >
                {({ selected }) => (
                  <>
                    <CustomRadio
                      isChecked={sort.value === sortSelected.value}
                      positionClass="left-4 top-1/2"
                    />
                    <span
                      className={`block truncate ${
                        selected ? 'font-medium' : 'font-normal'
                      }`}
                    >
                      {sort.name}
                    </span>
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

export default SortSelect;
